
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Row, Col, Card, Table, Button, Input, notification, Modal, Drawer,
  Select, Typography, Space, Tag, Tooltip
} from "antd";
import axios from "axios";
import { baseUrl } from "../../config";
import {
  DeleteOutlined, FilterOutlined, EditOutlined, ExclamationCircleOutlined, 
  PlusOutlined, BookOutlined, SearchOutlined, ReloadOutlined, EyeOutlined
} from "@ant-design/icons";
import { hasPermission } from '../../components/permissions';
import moment from 'moment';
import PurposesForm from './Form';
import PurposesView from './View';

const { confirm } = Modal;
const { Option } = Select;
const { Title, Text } = Typography;

function PurposesList() {
  const history = useHistory();
  const [purposesList, setPurposesList] = useState([]);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [sortField, setSortField] = useState('id');
  const [sortOrder, setSortOrder] = useState('desc');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  
  const [filters, setFilters] = useState({
    category: '',
    status: '',
    is_recurring: '',
    requires_approval: ''
  });

  const [purposeCategories, setPurposeCategories] = useState([]);

  // Modal states
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [currentPurposeId, setCurrentPurposeId] = useState(null);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [showFilter, setShowFilter] = useState(true);

  const columns = [
    {
      title: '#',
      dataIndex: "key",
      key: "key",
      fixed: 'left',
      width: 60,
      align: 'center',
    },
    {
      title: 'Code',
      dataIndex: "code",
      key: "code",
      fixed: 'left',
      width: 120,
      render: (code) => <Text strong style={{ color: '#7a0000' }}>{code}</Text>
    },
    {
      title: 'Purpose Name',
      dataIndex: "name",
      key: "name",
      width: 200,
      sorter: true,
      render: (name) => <Text strong>{name}</Text>
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 150,
      render: (category) => {
        const colors = {
          'Student_Support': 'blue',
          'Staff_Compensation': 'green',
          'Infrastructure': 'orange',
          'Operations': 'purple',
          'Medical_Aid': 'red',
          'Education': 'cyan',
          'Technology': 'geekblue',
          'Marketing': 'magenta',
          'Legal': 'volcano',
          'Other': 'default'
        };
        return <Tag color={colors[category] || 'default'}>{category?.replace('_', ' ')}</Tag>;
      }
    },
    {
      title: 'Sub Category',
      dataIndex: 'sub_category',
      key: 'sub_category',
      width: 130,
      render: (subCategory) => subCategory || '-'
    },
    {
      title: 'Max Amount',
      dataIndex: 'max_amount_limit',
      key: 'max_amount_limit',
      width: 120,
      render: (amount) => amount ? `₹${Number(amount).toLocaleString('en-IN')}` : 'No Limit'
    },
    {
      title: 'Recurring',
      dataIndex: 'is_recurring',
      key: 'is_recurring',
      width: 100,
      align: 'center',
      render: (isRecurring) => (
        <Tag color={isRecurring ? 'success' : 'default'}>
          {isRecurring ? 'Yes' : 'No'}
        </Tag>
      )
    },
    {
      title: 'Approval',
      dataIndex: 'requires_approval',
      key: 'requires_approval',
      width: 100,
      align: 'center',
      render: (requiresApproval) => (
        <Tag color={requiresApproval ? 'warning' : 'success'}>
          {requiresApproval ? 'Required' : 'Auto'}
        </Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={status === 'Active' ? 'success' : 'default'}>
          {status}
        </Tag>
      )
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date) => date ? moment(date).format('DD MMM YYYY') : '-'
    },
    {
      title: 'Actions',
      key: "action",
      dataIndex: "action",
      fixed: 'right',
      width: 150,
      align: 'center',
    }
  ];

  useEffect(() => {
    getPurposesList();
    loadPurposeCategories();
  }, []);

  const loadPurposeCategories = async () => {
    try {
      const res = await axios.get(`${baseUrl}/purpose-categories/list`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
      });
      if (res.data.status) setPurposeCategories(res.data.result);
    } catch (_) {}
  };

  const getPurposesList = async (page = 1, limit = 20, sortField = 'id', sortOrder = 'desc', search = '', filters = {}) => {
    try {
      setLoading(true);
      const sortParam = `&sortField=${sortField}&sortOrder=${sortOrder}`;
      const searchParam = search ? `&search=${search}` : '';
      const filterParams = Object.keys(filters)
        .filter(key => filters[key])
        .map(key => `${key}=${filters[key]}`)
        .join('&');
      
      const response = await axios.get(
        `${baseUrl}/purposes/list?page=${page}&limit=${limit}${sortParam}${searchParam}&${filterParams}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          }
        }
      );
      
      if (response.data.status) {
        setPurposesList(response.data.result);
        setPagination({
          current: page,
          pageSize: limit,
          total: response.data.totalCount
        });
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to fetch purposes data',
        placement: 'topRight'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(`${baseUrl}/purposes/delete/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (response.status === 200) {
        getPurposesList(pagination.current, pagination.pageSize, sortField, sortOrder, search, filters);
        notification.success({
          message: 'Success',
          description: 'Purpose deleted successfully!',
          placement: 'topRight'
        });
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to delete purpose',
        placement: 'topRight'
      });
    }
  };

  const showDeleteConfirm = (id, name) => {
    confirm({
      title: 'Delete Purpose',
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      content: `Are you sure you want to delete "${name}"?`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        handleDelete(id);
      },
    });
  };

  const showFormModal = (purposeId = null) => {
    setCurrentPurposeId(purposeId);
    setIsUpdateMode(!!purposeId);
    setFormModalVisible(true);
  };

  const showViewModal = (purposeId) => {
    setCurrentPurposeId(purposeId);
    setViewModalVisible(true);
  };

  const closeFormModal = () => {
    setFormModalVisible(false);
    setCurrentPurposeId(null);
    setIsUpdateMode(false);
  };

  const closeViewModal = () => {
    setViewModalVisible(false);
    setCurrentPurposeId(null);
  };

  const onRefresh = () => {
    closeFormModal();
    getPurposesList(currentPage, limit, sortField, sortOrder, search, filters);
  };

  const handleEditFromView = (purposeId) => {
    closeViewModal();
    showFormModal(purposeId);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const { current, pageSize } = pagination;
    const { field, order } = sorter;
    const formattedOrder = order === 'ascend' ? 'asc' : 'desc';
    
    setSortField(field);
    setSortOrder(formattedOrder);
    setCurrentPage(current);
    setLimit(pageSize);
    
    getPurposesList(current, pageSize, field, formattedOrder, search, filters);
  };

  const handleSearch = () => {
    getPurposesList(1, limit, sortField, sortOrder, search, filters);
  };

  const handleResetFilters = () => {
    const resetFilters = { category: '', status: '', is_recurring: '', requires_approval: '' };
    setFilters(resetFilters);
    setSearch('');
    getPurposesList(1, limit, sortField, sortOrder, '', resetFilters);
  };

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col xs="24" xl={24}>
          <Card
            bordered={false}
            className="criclebox tablespace mb-24"
            title={<span style={{ color: '#7a0000', fontWeight: 700, fontSize: 14 }}>Purpose Categories</span>}
            headStyle={{ borderBottom: '1.5px solid #e2e8f0' }}
            extra={
              <Space size="small">
                {hasPermission('/purposes', 'createPermission') && (
                  <Button icon={<PlusOutlined />} onClick={() => showFormModal()}
                    style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff', fontWeight: 600 }}>
                    Add Purpose
                  </Button>
                )}
              </Space>
            }
          >
            <div className="table-responsive">
              <Table
                columns={columns}
                loading={loading}
                scroll={{ x: 'max-content' }}
                bordered
                size="middle"
                title={() => (
                  <div style={{ marginBottom: '16px' }}>
                    <Row gutter={[16, 8]} align="middle">
                      <Col>
                        <Button
                          icon={<FilterOutlined />}
                          onClick={() => setShowFilter(!showFilter)}
                          style={{ borderRadius: '6px' }}
                        >
                          {showFilter ? 'Hide Filters' : 'Show Filters'}
                        </Button>
                      </Col>
                      <Col>
                        <Input
                          placeholder="Search by name, code..."
                          value={search}
                          onChange={e => setSearch(e.target.value)}
                          onPressEnter={handleSearch}
                          style={{ width: 300, borderRadius: '6px' }}
                          suffix={<SearchOutlined onClick={handleSearch} style={{ cursor: 'pointer' }} />}
                        />
                      </Col>
                    </Row>

                    {showFilter && (
                      <div style={{
                        marginTop: '12px',
                        padding: '16px',
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #e9ecef'
                      }}>
                        <Row gutter={[12, 8]}>
                          <Col span={6}>
                            <Select
                              placeholder="Category"
                              value={filters.category || undefined}
                              onChange={value => setFilters({...filters, category: value || ''})}
                              style={{ width: '100%' }}
                              allowClear
                            >
                              {purposeCategories.map(c => (
                                <Option key={c.id} value={c.name}>{c.name.replace(/_/g, ' ')}</Option>
                              ))}
                            </Select>
                          </Col>
                          <Col span={6}>
                            <Select
                              placeholder="Status"
                              value={filters.status}
                              onChange={value => setFilters({...filters, status: value})}
                              style={{ width: '100%' }}
                              allowClear
                            >
                              <Option value="">All Status</Option>
                              <Option value="Active">Active</Option>
                              <Option value="Inactive">Inactive</Option>
                            </Select>
                          </Col>
                          <Col span={6}>
                            <Select
                              placeholder="Recurring"
                              value={filters.is_recurring}
                              onChange={value => setFilters({...filters, is_recurring: value})}
                              style={{ width: '100%' }}
                              allowClear
                            >
                              <Option value="">All Types</Option>
                              <Option value="true">Recurring</Option>
                              <Option value="false">One-time</Option>
                            </Select>
                          </Col>
                          <Col span={6}>
                            <Select
                              placeholder="Approval"
                              value={filters.requires_approval}
                              onChange={value => setFilters({...filters, requires_approval: value})}
                              style={{ width: '100%' }}
                              allowClear
                            >
                              <Option value="">All Types</Option>
                              <Option value="true">Requires Approval</Option>
                              <Option value="false">Auto Approve</Option>
                            </Select>
                          </Col>
                          <Col span={24}>
                            <Space style={{ marginTop: '8px' }}>
                              <Button
                                icon={<SearchOutlined />}
                                onClick={handleSearch}
                                style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff', borderRadius: '6px' }}
                              >
                                Search
                              </Button>
                              <Button
                                icon={<ReloadOutlined />}
                                onClick={handleResetFilters}
                                style={{ borderRadius: '6px' }}
                              >
                                Reset Filters
                              </Button>
                            </Space>
                          </Col>
                        </Row>
                      </div>
                    )}
                  </div>
                )}
                dataSource={purposesList.map((purpose, index) => ({
                  key: (limit * (currentPage - 1)) + index + 1,
                  ...purpose,
                  action: (
                    <Space size={4}>
                      <Button size="small" icon={<EyeOutlined />} onClick={() => showViewModal(purpose.id)}>View</Button>
                      {hasPermission('/purposes', 'editPermission') && (
                        <Button size="small" icon={<EditOutlined />} onClick={() => showFormModal(purpose.id)}
                          style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}>Edit</Button>
                      )}
                      {hasPermission('/purposes', 'deletePermission') && (
                        <Button danger size="small" icon={<DeleteOutlined />} onClick={() => showDeleteConfirm(purpose.id, purpose.name)}>Delete</Button>
                      )}
                    </Space>
                  )
                }))}
                pagination={{
                  ...pagination,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} purposes`,
                  showSizeChanger: true,
                  pageSizeOptions: ['20', '50', '100'],
                }}
                onChange={handleTableChange}
              />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Form Drawer */}
      <Drawer
        title={<span style={{ fontWeight: 700, color: '#7a0000' }}>{isUpdateMode ? "Update Purpose" : "Add Purpose"}</span>}
        width={720}
        open={formModalVisible}
        onClose={closeFormModal}
        destroyOnClose
      >
        <PurposesForm
          purposeId={currentPurposeId}
          onRefreshData={onRefresh}
        />
      </Drawer>

      {/* View Drawer */}
      <Drawer
        title={<span style={{ fontWeight: 700, color: '#7a0000' }}>Purpose Details</span>}
        width={900}
        open={viewModalVisible}
        onClose={closeViewModal}
        destroyOnClose
      >
        <PurposesView
          purposeId={currentPurposeId}
          onEdit={handleEditFromView}
          isModal={true}
        />
      </Drawer>
    </div>
  );
}

export default PurposesList;